import React, { useState, useEffect, useCallback } from "react";
import {
  FaEdit,
  FaPlus,
  FaSearch,
  FaMapMarkerAlt,
  FaBarcode,
} from "react-icons/fa";
import { useAuth } from "../context/Authcontext/AuthContext";
import Sidebar from "../components/SideBar";
import LocationForm from "./LocationSection/LocationForm";
import {
  addLocation,
  getLocations,
  updateLocation,
} from "./LocationSection/locationsfunction";
import { LocationCard } from "./LocationSection/LocationCard";
import { UserData } from "../Functions/firebaseFunction";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import BranchStaffModal from "./StaffManagement/BranchStaffModal";
import { useNavigate } from "react-router-dom";
import useStore from "../store/useStore";
import BuyScanGunModal from "../components/BuyScanGunModal";
import TrackOrderModal from "../components/TrackOrderModal";

// ✅ ENHANCED LocationCard component

const Branches = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  console.log("Current User in Branches:", currentUser);

  const [showProfileAlertModal, setShowProfileAlertModal] = useState(false);
  const [showScanGunModal, setShowScanGunModal] = useState(false);
  const [showTrackOrderModal, setShowTrackOrderModal] = useState(false);
  const [activeOrders, setActiveOrders] = useState([]);
  
  const userData = useStore((state) => state.userData);
  const setUserData = useStore((state) => state.setUserData);
  const locations = useStore((state) => state.locations);
  const setLocations = useStore((state) => state.setLocations);
  const locationsLoaded = useStore((state) => state.locationsLoaded);

  const [state, setState] = useState({
    showPopup: false,
    locations: locations || [],
    searchQuery: "",
    isEditing: false,
    editingId: null,
    loadError: null,
    isLoading: !locationsLoaded,
  });

  const [formData, setFormData] = useState({
    // Step 1
    name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    locationId: "",
    contact: "",
    suite: "",
    openTime: "", // Added
    closeTime: "", // Added
    // Step 2
    businessName: "",
    businessIdNumber: "", // Tax ID
    verificationDocuments: [
      { id: "ein", type: "EIN_Certificate", file: null, previewUrl: null },
      { id: "license", type: "Business_License", file: null, previewUrl: null },
      { id: "proof", type: "Proof_of_Address", file: null, previewUrl: null },
    ],
    storeImages: [],
  });

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [staffModalBranchId, setStaffModalBranchId] = useState(null);

  const updateState = useCallback(
    (updates) => setState((prev) => ({ ...prev, ...updates })),
    []
  );

  // 🔹 Fetch all locations and user data
  useEffect(() => {
    if (currentUser) {
      const fetchData = async () => {
        try {
          const [result, user] = await Promise.all([
            getLocations(),
            UserData()
          ]);
          setLocations(result);
          updateState({ locations: result, loadError: null, isLoading: false });
          setUserData(user);
        } catch (error) {
          updateState({ loadError: error.message, isLoading: false });
        }
      };
      fetchData();
    }
  }, [currentUser, updateState]);

  // Fetch active scan gun orders
  useEffect(() => {
    if (!currentUser?.uid) return;
    
    const q = query(
      collection(db, "scanGunOrders"),
      where("vendorId", "==", currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const pendingOrders = orders.filter(o => o.status !== "delivered" && o.status !== "payment_pending");
      setActiveOrders(pendingOrders);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // 🔹 Remove buggy useEffect, we will initialize verification docs directly in state and clearFormData

  // 🔹 Handle inputs
  const handleInputChange = (field, value) => {
    let newFormData = { ...formData, [field]: value };
    if (field === "contact") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      const formatted = digits.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
      newFormData = { ...formData, contact: formatted };
    }
    setFormData(newFormData);
  };

  // 🔹 Clear all data
const clearFormData = useCallback(() => {
    // Revoke object URLs to prevent memory leaks
    formData.verificationDocuments?.forEach((d) => {
      if (d?.previewUrl) URL.revokeObjectURL(d.previewUrl);
    });
    formData.storeImages?.forEach((img) => {
      if (img?.previewUrl) URL.revokeObjectURL(img.previewUrl);
    });

    setFormData({
      name: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      locationId: "",
      contact: "",
      suite: "",
      openTime: "",
      closeTime: "",
      businessName: "",
      businessIdNumber: "",
      verificationDocuments: [
        { id: "ein", type: "EIN_Certificate", file: null, previewUrl: null },
        { id: "license", type: "Business_License", file: null, previewUrl: null },
        { id: "proof", type: "Proof_of_Address", file: null, previewUrl: null },
      ],
      storeImages: [],
    });
    setStep(1);
    updateState({ isEditing: false, editingId: null });
  }, [formData, updateState]);

  // 🔹 Validation checks
// 🔹 Validation checks
  const isStep1Valid = () => {
    const {
      name,
      address,
      city,
      state: st,
      zip,
      locationId,
      contact,
      openTime,
      closeTime,
    } = formData;

    return (
      name?.trim() &&
      address?.trim() &&
      city?.trim() &&
      st?.trim() &&
      zip?.trim() &&
      locationId?.trim() &&
      contact?.trim().length > 0 &&
      openTime?.trim() && // Check Open Time
      closeTime?.trim()   // Check Close Time
    );
  };

  const isStep2Valid = () => {
    const { businessName, businessIdNumber, verificationDocuments } = formData;

    // 1. Check Text Fields
    const hasLegalInfo = businessName?.trim() && businessIdNumber?.trim();

    // 2. Check Documents (Must have EIN, License, and Proof)
    // We check if the specific ID exists in the array AND has a file/url attached
    const requiredDocs = ["ein", "license", "proof"];
    const hasAllDocs = requiredDocs.every((type) => {
      const doc = verificationDocuments.find((d) => d.id === type);
      return doc && (doc.file || doc.previewUrl);
    });

    return hasLegalInfo && hasAllDocs;
  };

  // 🔹 File handlers
  const handleFileChange = (e, slotId) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const allowed =
      file.type.startsWith("image/") || file.type === "application/pdf";
    if (!allowed) return alert("Please upload an image or PDF file.");

    if (file.size > 10 * 1024 * 1024)
      return alert("File size must be under 10MB.");

    const previewUrl = file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : null;

    setFormData((prev) => {
      const docs = prev.verificationDocuments || [];
      const exists = docs.some((d) => d.id === slotId);
      
      let newDocs;
      if (exists) {
        newDocs = docs.map((d) =>
          d.id === slotId ? { ...d, file, previewUrl, name: file.name } : d
        );
      } else {
        newDocs = [
          ...docs,
          {
            id: slotId,
            type: slotId === "ein" ? "EIN_Certificate" : slotId === "license" ? "Business_License" : "Proof_of_Address",
            file,
            previewUrl,
            name: file.name
          }
        ];
      }

      return {
        ...prev,
        verificationDocuments: newDocs,
      };
    });
  };

  const removeDocument = (slotId) => {
    setFormData((prev) => ({
      ...prev,
      verificationDocuments: prev.verificationDocuments.map((d) =>
        d.id === slotId ? { ...d, file: null, previewUrl: null, name: null } : d
      ),
    }));
  };

  // 🔹 Store image handlers
  const handleStoreImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validFiles = files.filter((file) => file.type.startsWith("image/"));
    const total = formData.storeImages.length + validFiles.length;
    if (total > 6) return alert("You can upload up to 6 images only.");

    const newImages = validFiles.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
    }));

    setFormData((prev) => ({
      ...prev,
      storeImages: [...prev.storeImages, ...newImages],
    }));
  };

  const removeStoreImage = (index) => {
    setFormData((prev) => {
      const updated = [...prev.storeImages];
      const removed = updated.splice(index, 1)[0];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return { ...prev, storeImages: updated };
    });
  };

  // 🔹 Submit handler
// 🔹 Submit handler
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Step 1 Validation
    if (step === 1) {
      if (!isStep1Valid()) return alert("Please fill all required fields.");
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Step 2 Validation & Submission
    if (step === 2) {
      if (!isStep2Valid())
        return alert("Please complete business verification details.");

      setIsSubmitting(true);

      // --- 1. Robust Name Helper ---
      const getOwnerName = () => {
        // Option A: If Firestore userData has firstName/lastName
        if (userData?.firstName || userData?.lastName) {
            return `${userData.firstName || ""} ${userData.lastName || ""}`.trim();
        }
        // Option B: If context has firstName/lastName (less likely, but fallback)
        if (currentUser?.firstName || currentUser?.lastName) {
            return `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim();
        }
        // Option C: Fallback to standard displayName
        if (currentUser?.displayName) {
            return currentUser.displayName;
        }
        // Option D: Fallback to email username
        if (currentUser?.email) {
            return currentUser.email.split('@')[0];
        }
        return "Unknown Vendor";
      };

      try {
        const submissionPayload = {
          ownerId: currentUser?.uid || null,
          ownerEmail: currentUser?.email || null,
          ownerName: getOwnerName(), // <--- Use the helper here
          ...formData,
          status: state.isEditing ? formData.status : "pending",
          submittedAt: new Date(),
        };

        if (state.isEditing && state.editingId) {
          // Update existing location
          await updateLocation(state.editingId, submissionPayload);
          updateState({
            locations: state.locations.map((loc) =>
              loc.id === state.editingId ? { ...loc, ...submissionPayload } : loc
            ),
          });
        } else {
          // Create new location
          const newDocRef = await addLocation(submissionPayload);
          if (newDocRef?.id) {
            updateState({
              locations: [
                ...state.locations,
                { id: newDocRef.id, ...submissionPayload },
              ],
            });
            setTimeout(() => {
              alert("Branch Created Successfully!\n\nIMPORTANT: Please remember to add at least one Manager to this branch using the 'Staff' button. The branch will not be fully active without a manager.");
            }, 500);
          }
        }

        clearFormData();
        updateState({ showPopup: false, isEditing: false, editingId: null });
      } catch (err) {
        console.error("Error submitting:", err);
        // This logs the exact error to your browser console (F12)
        alert(`Error: ${err.message}`); 
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // 🔹 Edit
  const handleEditLocation = (id) => {
    const locationToEdit = state.locations.find((loc) => loc.id === id);
    if (locationToEdit) {
      // Logic Guard: Prevent editing pending stores
      if (locationToEdit.status === "pending") {
        return alert("This branch is currently pending verification and cannot be edited.");
      }

      const mappedDocs =
        (locationToEdit.verificationDocuments || []).map((d, idx) => ({
          id: d.id || `doc-${idx}`,
          type: d.type,
          file: null,
          previewUrl: d.url || null,
          name: d.name || null,
        })) || [];

      setFormData({
        ...formData,
        ...locationToEdit,
        verificationDocuments: mappedDocs,
      });
      updateState({ showPopup: true, isEditing: true, editingId: id });
      setStep(1);
    }
  };

  const handleOpenStaffModal = (id) => {
    const loc = state.locations.find(l => l.id === id);
    if (!loc) return;
    if (loc.status !== "approved") {
      return alert("Staff management is only available for approved branches.");
    }
    setStaffModalBranchId(id);
  };

  // 🔹 Filtered list
  const filteredLocations = state.locations.filter(
    (loc) =>
      (loc.name &&
        loc.name.toLowerCase().includes(state.searchQuery.toLowerCase())) ||
      (loc.address &&
        loc.address.toLowerCase().includes(state.searchQuery.toLowerCase())) ||
      (loc.city &&
        loc.city.toLowerCase().includes(state.searchQuery.toLowerCase())) ||
      (loc.locationId &&
        loc.locationId.toLowerCase().includes(state.searchQuery.toLowerCase()))
  );

  // 🔹 Render

  const LocationCardShimmer = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
      <div className="flex items-center space-x-4 mb-4">
        <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
      <div className="space-y-2 mb-4">
        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
      </div>
      <div className="flex justify-between mt-4">
        <div className="h-8 w-20 bg-gray-200 rounded"></div>
        <div className="h-8 w-20 bg-gray-200 rounded"></div>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <main className="ml-80 p-8 lg:p-10 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Branches</h1>
            <p className="text-gray-600 mt-1">Manage your business branches and their staff</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, address, city..."
                className="pl-10 pr-4 py-2 w-80 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={state.searchQuery}
                onChange={(e) => updateState({ searchQuery: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="flex space-x-4">
            <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-100">
              <FaMapMarkerAlt className="text-green-500" />
              <span className="text-gray-600">Total Branches:</span>
              <span className="font-semibold text-gray-900">
                {state.locations.length}
              </span>
            </div>
            
            <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-100">
              <FaBarcode className="text-blue-500" />
              <span className="text-gray-600">Scan Guns Owned:</span>
              <span className="font-semibold text-gray-900 text-lg">
                {userData?.scanGunCount || 0}
              </span>
            </div>
          </div>
          <div className="flex space-x-3">
            {activeOrders.length > 0 && (
              <button
                onClick={() => setShowTrackOrderModal(true)}
                className="flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors shadow-sm border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              >
                <span>Track Order ({activeOrders.length})</span>
              </button>
            )}
            <button
              onClick={() => setShowScanGunModal(true)}
              className="flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors shadow-sm bg-gray-900 hover:bg-black text-white"
            >
              <FaPlus />
              <span>Buy Scan Gun</span>
            </button>
            <button
              onClick={() => {
                const hasProfileInfo = userData?.firstName && userData?.lastName && userData?.mobile;
                const hasPaymentInfo = !!userData?.stripeAccountId;

                if (!hasProfileInfo || !hasPaymentInfo) {
                  setShowProfileAlertModal(true);
                  return;
                }

                clearFormData();
                updateState({ showPopup: true });
              }}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors shadow-sm ${
                (!userData?.firstName || !userData?.stripeAccountId) 
                  ? "bg-gray-400 cursor-not-allowed text-white opacity-80" 
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              <FaPlus />
              <span>Add New Branch</span>
            </button>
          </div>
        </div>

        {/* Location Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {state.isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <LocationCardShimmer key={i} />
            ))
          ) : filteredLocations.length ? (
            filteredLocations.map((location) => (
              <LocationCard
                key={location.id}
                location={location}
                onEdit={handleEditLocation}
                onManageStaff={handleOpenStaffModal}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <FaMapMarkerAlt className="mx-auto text-4xl text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No branches found
              </h3>
              <p className="text-gray-500">
                Get started by adding your first branch.
              </p>
            </div>
          )}
        </div>

        {/* Form Modal */}
        {state.showPopup && (
          <LocationForm
            step={step}
            state={state}
            formData={formData}
            isSubmitting={isSubmitting}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
            removeDocument={removeDocument}
            handleFormSubmit={handleFormSubmit}
            isStep1Valid={isStep1Valid}
            isStep2Valid={isStep2Valid}
            setStep={setStep}
            updateState={updateState}
            handleStoreImagesChange={handleStoreImagesChange}
            removeStoreImage={removeStoreImage}
          />
        )}

        {/* Staff Management Modal overlay */}
        {staffModalBranchId && (
          <BranchStaffModal
            locationId={staffModalBranchId}
            onClose={() => setStaffModalBranchId(null)}
          />
        )}

        {/* Buy Scan Gun Modal */}
        <BuyScanGunModal
          isOpen={showScanGunModal}
          onClose={() => setShowScanGunModal(false)}
          userEmail={currentUser?.email}
          userName={userData ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim() : ""}
        />

        {/* Track Order Modal */}
        <TrackOrderModal
          isOpen={showTrackOrderModal}
          onClose={() => setShowTrackOrderModal(false)}
          orders={activeOrders}
        />

        {/* Custom Profile Completion Alert Modal */}
        {showProfileAlertModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md relative transform transition-all">
              {/* Close 'X' Button */}
              <button
                onClick={() => setShowProfileAlertModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="text-center mt-2">
                <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-100 mb-4">
                   <svg className="h-7 w-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                   </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Action Required</h3>
                <p className="text-gray-600 mb-8 px-2 leading-relaxed">
                  Please navigate to Settings and complete your Profile details (Name, Mobile) AND connect your Stripe Account before creating business branches.
                </p>

                {/* Bottom Center Button */}
                <div className="flex justify-center">
                  <button
                    onClick={() => {
                      setShowProfileAlertModal(false);
                      navigate("/settings");
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-xl shadow-md transition-all duration-200 transform hover:scale-105 w-full"
                  >
                    Go to Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </main>
    </div>
  );
};

export default Branches;
